/**
 * Utilitaires pour la génération d'emails HTML
 */

/**
 * Récupère tous les styles CSS calculés d'un élément et de ses enfants
 */
export const getComputedStyles = (element: HTMLElement): string => {
  const styles = window.getComputedStyle(element);
  let cssText = "";

  for (let i = 0; i < styles.length; i++) {
    const property = styles[i];
    const value = styles.getPropertyValue(property);
    if (value && value !== "initial" && value !== "normal") {
      cssText += `${property}: ${value}; `;
    }
  }

  return cssText;
};

/**
 * Applique les styles inline à un élément et tous ses enfants
 */
export const applyInlineStyles = (element: HTMLElement): HTMLElement => {
  const clone = element.cloneNode(true) as HTMLElement;

  // Ajouter temporairement le clone au DOM pour pouvoir calculer les styles
  const tempContainer = document.createElement("div");
  tempContainer.style.position = "absolute";
  tempContainer.style.left = "-9999px";
  tempContainer.style.top = "-9999px";
  tempContainer.appendChild(clone);
  document.body.appendChild(tempContainer);

  try {
    // Appliquer les styles calculés à l'élément principal
    const computedStyles = getComputedStyles(clone);
    clone.style.cssText = computedStyles;

    // Appliquer les styles aux enfants
    const children = clone.querySelectorAll("*");
    children.forEach((child, index) => {
      const originalChild = element.querySelectorAll("*")[index] as HTMLElement;
      if (originalChild) {
        const childStyles = getComputedStyles(child as HTMLElement);
        (child as HTMLElement).style.cssText = childStyles;
      }
    });

    // Retirer le clone du DOM temporaire
    document.body.removeChild(tempContainer);
    return clone;
  } catch (error) {
    // En cas d'erreur, nettoyer et retourner le clone sans styles
    if (document.body.contains(tempContainer)) {
      document.body.removeChild(tempContainer);
    }
    console.warn("Erreur lors de l'application des styles inline:", error);
    return clone;
  }
};

/**
 * Nettoie le HTML pour l'email en supprimant les éléments non nécessaires
 */
export const cleanHtmlForEmail = (html: string): string => {
  console.log("=== DÉBUT NETTOYAGE HTML ===");
  console.log("HTML original length:", html.length);

  // Supprimer les scripts
  html = html.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    ""
  );

  // Supprimer les éléments avec des classes de développement
  html = html.replace(
    /class="[^"]*(?:preview-mode|pdf-capture-mode)[^"]*"/gi,
    ""
  );

  // Supprimer les attributs contenteditable
  html = html.replace(/\s*contenteditable="[^"]*"/gi, "");

  // Supprimer les événements onclick, onchange, etc.
  html = html.replace(/\s*on\w+="[^"]*"/gi, "");

  // Supprimer UNIQUEMENT les boutons de contrôle spécifiques (pas les liens sociaux)
  html = html.replace(
    /<button[^>]*class="[^"]*(?:btn-control|preview-toggle)[^"]*"[^>]*>.*?<\/button>/gi,
    ""
  );

  // Supprimer les boutons de repiquage vides (mais garder le contenu du footer)
  html = html.replace(
    /<button[^>]*class="[^"]*preview-mode-button[^"]*"[^>]*>.*?Ajouter un repiquage adresse\(s\).*?<\/button>/gi,
    ""
  );

  // Supprimer UNIQUEMENT les zones de drop vides (pas celles avec du contenu)
  html = html.replace(
    /<div[^>]*class="[^"]*product-zone[^"]*"[^>]*>\s*<div[^>]*>Déposez un produit ici<\/div>\s*<\/div>/gi,
    ""
  );

  // Nettoyer les styles de bordure et de contrôle (mais garder les styles importants)
  html = html.replace(
    /style="[^"]*(?:border:\s*[^;]*;|outline:\s*[^;]*;|box-shadow:\s*[^;]*;)[^"]*"/gi,
    (match) => {
      let cleaned = match
        .replace(/border:\s*[^;]*;/gi, "border: none;")
        .replace(/outline:\s*[^;]*;/gi, "outline: none;")
        .replace(/box-shadow:\s*[^;]*;/gi, "box-shadow: none;");

      // S'assurer que les styles importants sont préservés
      if (!cleaned.includes("background-color")) {
        cleaned = cleaned.replace('style="', 'style="background-color:; ');
      }
      if (!cleaned.includes("color")) {
        cleaned = cleaned.replace('style="', 'style="color: #333; ');
      }

      return cleaned;
    }
  );

  // SUPPRIMER TOUTES LES BORDURES GRISES - Nettoyage agressif
  html = html.replace(/border:\s*[^;]*;/gi, "border: none;");
  html = html.replace(/border-style:\s*[^;]*;/gi, "border-style: none;");
  html = html.replace(/border-color:\s*[^;]*;/gi, "border-color: transparent;");
  html = html.replace(/border-width:\s*[^;]*;/gi, "border-width: 0;");
  html = html.replace(/border-radius:\s*[^;]*;/gi, "border-radius: 0;");
  html = html.replace(/outline:\s*[^;]*;/gi, "outline: none;");
  html = html.replace(/box-shadow:\s*[^;]*;/gi, "box-shadow: none;");

  // Supprimer les backgrounds gris
  html = html.replace(
    /background-color:\s*#f8f8f8/gi,
    "background-color: transparent"
  );
  html = html.replace(
    /background-color:\s*rgb\(248,\s*248,\s*248\)/gi,
    "background-color: transparent"
  );
  html = html.replace(/background:\s*#f8f8f8/gi, "background: transparent");

  // Supprimer les classes qui causent des bordures
  html = html.replace(
    /class="[^"]*(?:drop-active|hover|selected)[^"]*"/gi,
    (match) => {
      return match.replace(/(?:drop-active|hover|selected)/g, "");
    }
  );

  // Vérifier que les éléments importants sont toujours présents
  const hasFooter = html.includes("support-footer");
  const hasSocialLinks = html.includes("text-[#B70018]");
  const hasValidityBanner = html.includes("validity-banner");
  const hasAgcoLogo = html.includes("logo-agco.svg");
  const hasFendtLogo = html.includes("logo-fendt.svg");
  const hasValtraLogo = html.includes("logo-valtra");
  const hasHvLogo = html.includes("logo-hv.svg");
  const hasValidityText = html.includes("valables") || html.includes("validité") || html.includes("XX");

  console.log("=== VÉRIFICATIONS FOOTER ===");
  console.log("Footer présent:", hasFooter);
  console.log("Liens sociaux présents:", hasSocialLinks);
  console.log("Bandeau validité présent:", hasValidityBanner);
  console.log("Logo AGCO présent:", hasAgcoLogo);
  console.log("Logo Fendt présent:", hasFendtLogo);
  console.log("Logo Valtra présent:", hasValtraLogo);
  console.log("Logo HV présent:", hasHvLogo);
  console.log("Texte de validité présent:", hasValidityText);
  console.log("HTML final length:", html.length);
  console.log("=== FIN NETTOYAGE HTML ===");

  return html;
};

/**
 * Convertit les URLs d'images relatives en URLs absolues
 */
export const convertImageUrlsToAbsolute = (html: string): string => {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3000";

  // Convertir les URLs d'images de produits
  html = html.replace(
    /src="([^"]*\/products\/[^"]*)"/gi,
    (_match, relativePath) => {
      const imageName = relativePath.split("/").pop();
      return `src="${apiUrl}/upl/products/${imageName}"`;
    }
  );

  // Convertir les URLs d'images de headers
  html = html.replace(
    /src="([^"]*\/headers\/[^"]*)"/gi,
    (_match, relativePath) => {
      const imageName = relativePath.split("/").pop();
      return `src="${apiUrl}/upl/headers/${imageName}"`;
    }
  );

  // Convertir les URLs d'images de pictogrammes
  html = html.replace(
    /src="([^"]*\/pictograms\/[^"]*)"/gi,
    (_match, relativePath) => {
      const imageName = relativePath.split("/").pop();
      return `src="${apiUrl}/upl/pictograms/${imageName}"`;
    }
  );

  // Convertir les URLs d'images de logos (tous les logos de marques)
  html = html.replace(
    /src="([^"]*\/logos\/[^"]*)"/gi,
    (_match, relativePath) => {
      const imageName = relativePath.split("/").pop();
      return `src="${apiUrl}/upl/logos/${imageName}"`;
    }
  );
  
  // Convertir spécifiquement les logos de marques depuis /images/
  const brandLogos = ['logo-agco.svg', 'logo-fendt.svg', 'logo-valtra.svg', 'logo-valtra-black.svg', 'logo-hv.svg'];
  brandLogos.forEach(logoName => {
    const logoPattern = new RegExp(`src="([^"]*\\/images\\/${logoName.replace('.', '\\.')})"`, 'gi');
    html = html.replace(logoPattern, `src="${apiUrl}/upl/assets/${logoName}"`);
  });

  // Convertir les URLs d'images génériques (fallback)
  html = html.replace(
    /src="([^"]*\/images\/[^"]*)"/gi,
    (_match, relativePath) => {
      const imageName = relativePath.split("/").pop();
      return `src="${apiUrl}/upl/images/${imageName}"`;
    }
  );

  // Gérer les URLs qui contiennent déjà l'API URL mais avec des chemins relatifs
  html = html.replace(
    new RegExp(
      `src="${apiUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}/[^"]*"`,
      "gi"
    ),
    (match) => {
      // Si l'URL contient déjà l'API URL, on la laisse telle quelle
      return match;
    }
  );

  // Gérer les URLs complètes qui ne sont pas des chemins relatifs
  html = html.replace(/src="(https?:\/\/[^"]*)"/gi, (match) => {
    // Si c'est déjà une URL complète, on la laisse telle quelle
    return match;
  });

  return html;
};

/**
 * Applique les données de personnalisation aux éléments du DOM
 */
const applyCustomizationToElements = (
  canvasElement: HTMLElement,
  customizations: { [productId: number]: any } = {}
) => {
  // Récupérer tous les blocs de produits
  const productBlocks = canvasElement.querySelectorAll(".product-block");

  productBlocks.forEach((block) => {
    const productElement = block as HTMLElement;

    // Essayer de récupérer l'ID du produit depuis l'élément
    const productId = getProductIdFromElement(productElement);
    const customization = productId ? customizations[productId] : null;

    // Appliquer les styles de prix avec une taille minimale
    const priceElements = productElement.querySelectorAll(
      ".price-ht-container, .price-ttc-container"
    );
    console.log(
      `Found ${priceElements.length} price elements for product ${productId}`
    );
    priceElements.forEach((priceEl) => {
      if (priceEl instanceof HTMLElement) {
        const priceScale = Math.max(customization?.priceScale || 1, 0.8); // Taille minimale de 0.8
        console.log(
          `Applying price scale ${priceScale} to element:`,
          priceEl.className
        );
        priceEl.style.transform = `scale(${priceScale})`;
        priceEl.style.transformOrigin = "left";
        priceEl.style.display = "flex"; // S'assurer que l'élément est en flex

        // Appliquer aussi les tailles de police directement
        const priceHtElements = priceEl.querySelectorAll(".price-ht");
        priceHtElements.forEach((priceHtEl) => {
          if (priceHtEl instanceof HTMLElement) {
            const baseSize =
              parseFloat(getComputedStyle(priceHtEl).fontSize) || 16;
            const adjustedSize = baseSize * priceScale;
            priceHtEl.style.fontSize = `${adjustedSize}px`;
          }
        });

        const priceTtcElements = priceEl.querySelectorAll(".price-ttc");
        priceTtcElements.forEach((priceTtcEl) => {
          if (priceTtcEl instanceof HTMLElement) {
            const baseSize =
              parseFloat(getComputedStyle(priceTtcEl).fontSize) || 12;
            const adjustedSize = baseSize * priceScale;
            priceTtcEl.style.fontSize = `${adjustedSize}px`;
          }
        });
      }
    });

    // Appliquer les styles de pictogrammes
    const pictoElements = productElement.querySelectorAll(".picto-image");
    pictoElements.forEach((pictoEl) => {
      if (pictoEl instanceof HTMLElement) {
        const pictoScale = customization?.pictoScale || 1;
        pictoEl.style.transform = `scale(${pictoScale})`;
        pictoEl.style.transformOrigin = "top right";
      }
    });

    // Appliquer les styles de marques
    const brandElements = productElement.querySelectorAll(".brand-image");
    brandElements.forEach((brandEl) => {
      if (brandEl instanceof HTMLElement) {
        const brandScale = customization?.brandScale || 1;
        brandEl.style.transform = `scale(${brandScale})`;
        brandEl.style.transformOrigin = "center";
      }
    });

    // Appliquer les couleurs de fond
    const imageContainers = productElement.querySelectorAll(".image-container");
    imageContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        const backgroundColor = customization?.backgroundColor || "transparent";
        container.style.backgroundColor = backgroundColor;
      }
    });
  });
};

/**
 * Tente de récupérer l'ID du produit depuis un élément DOM
 */
const getProductIdFromElement = (element: HTMLElement): number | null => {
  // Essayer de récupérer l'ID depuis les attributs data
  const productId = element.getAttribute("data-product-id");
  if (productId) {
    return parseInt(productId, 10);
  }

  // Essayer de récupérer depuis les classes
  const classes = element.className.split(" ");
  for (const className of classes) {
    if (className.startsWith("product-")) {
      const match = className.match(/product-(\d+)/);
      if (match) {
        return parseInt(match[1], 10);
      }
    }
  }

  return null;
};

/**
 * Génère un HTML email complet avec tous les styles intégrés en utilisant domtoimage
 */
export const generateCompleteEmailHtml = async (
  canvasElement: HTMLElement,
  title: string,
  brand: string = "agco",
  customizations: { [productId: number]: any } = {}
): Promise<string> => {
  // Utiliser la même approche que SupportCanvas.vue
  // Sauvegarder l'état actuel
  const originalStyles = {
    backgroundColor: canvasElement.style.backgroundColor,
    height: canvasElement.style.height,
    overflow: canvasElement.style.overflow,
    maxHeight: canvasElement.style.maxHeight,
    position: canvasElement.style.position,
    top: canvasElement.style.top,
    left: canvasElement.style.left,
    transform: canvasElement.style.transform,
  };

  // Appliquer les styles pour la capture (comme dans SupportCanvas.vue)
  canvasElement.style.backgroundColor = "#ffffff";
  canvasElement.style.height = "auto";
  canvasElement.style.overflow = "visible";
  canvasElement.style.maxHeight = "none";
  canvasElement.style.position = "static";
  canvasElement.style.top = "auto";
  canvasElement.style.left = "auto";
  canvasElement.style.transform = "none";

  // Ajouter la classe de capture PDF (comme dans SupportCanvas.vue)
  canvasElement.classList.add("pdf-capture-mode");

  // S'assurer que tous les éléments sont visibles (comme dans SupportCanvas.vue)
  const allElements = canvasElement.querySelectorAll("*");
  allElements.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.visibility = "visible";
      el.style.opacity = "1";
      el.style.display =
        el.style.display === "none" ? "block" : el.style.display;
    }
  });

  // Appliquer les données de personnalisation aux éléments
  applyCustomizationToElements(canvasElement, customizations);

  // Supprimer complètement les éléments de contrôle
  const controlsToRemove = canvasElement.querySelectorAll(
    ".btn-control, .preview-toggle"
  );
  controlsToRemove.forEach((el) => {
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  });

  // Nettoyer les zones de drop (garder le contenu mais supprimer les bordures)
  const dropZones = canvasElement.querySelectorAll(".product-zone");
  dropZones.forEach((zone) => {
    if (zone instanceof HTMLElement) {
      zone.style.border = "none";
      zone.style.borderStyle = "none";
      zone.style.borderColor = "transparent";
      zone.style.backgroundColor = "transparent";
      zone.style.padding = "0";
      zone.style.margin = "0";
      zone.style.boxShadow = "none";
      zone.style.outline = "none";
    }
  });

  // SUPPRIMER TOUTES LES BORDURES GRISES - Nettoyer tous les éléments
  const allElementsForCleaning = canvasElement.querySelectorAll("*");
  allElementsForCleaning.forEach((element) => {
    if (element instanceof HTMLElement) {
      // Supprimer toutes les bordures
      element.style.border = "none";
      element.style.borderStyle = "none";
      element.style.borderColor = "transparent";
      element.style.borderWidth = "0";
      element.style.borderRadius = "0";

      // Supprimer les outlines et box-shadows
      element.style.outline = "none";
      element.style.boxShadow = "none";

      // Supprimer les backgrounds gris
      if (
        element.style.backgroundColor === "rgb(248, 248, 248)" ||
        element.style.backgroundColor === "#f8f8f8" ||
        element.style.backgroundColor === "rgba(248, 248, 248, 1)"
      ) {
        element.style.backgroundColor = "transparent";
      }

      // Supprimer les classes qui peuvent causer des bordures
      element.classList.remove("drop-active", "hover", "selected");
    }
  });

  // Supprimer spécifiquement les boutons de repiquage avec bordures
  const repiquageButtons = canvasElement.querySelectorAll(
    ".preview-mode-button, [class*='repiquage']"
  );
  repiquageButtons.forEach((button) => {
    if (button.parentNode) {
      button.parentNode.removeChild(button);
    }
  });

  // Nettoyer les zones de pictogrammes et marques
  const pictoZones = canvasElement.querySelectorAll(
    ".picto-zone, .brand-zone-info"
  );
  pictoZones.forEach((zone) => {
    if (zone instanceof HTMLElement) {
      zone.style.border = "none";
      zone.style.backgroundColor = "transparent";
      zone.style.boxShadow = "none";
      zone.style.outline = "none";
    }
  });

  // CORRECTION: Nettoyer le header pour qu'il soit en 100%
  const header = canvasElement.querySelector(".support-header");
  if (header instanceof HTMLElement) {
    header.style.width = "100%";
    header.style.padding = "0";
    header.style.margin = "0";
    header.style.border = "none";
    header.style.outline = "none";
    header.style.boxShadow = "none";

    // S'assurer que l'image du header est en 100%
    const headerImg = header.querySelector("img");
    if (headerImg instanceof HTMLImageElement) {
      headerImg.style.width = "100%";
      headerImg.style.height = "auto";
      headerImg.style.display = "block";
      headerImg.style.margin = "0";
      headerImg.style.padding = "0";
    }
  }

  // Supprimer tous les boutons
  const buttons = canvasElement.querySelectorAll("button");
  buttons.forEach((button) => {
    if (button.parentNode) {
      button.parentNode.removeChild(button);
    }
  });

  // Supprimer les éléments de contrôle dans le header
  const headerControls = canvasElement.querySelectorAll(
    ".support-header button, .support-footer button"
  );
  headerControls.forEach((control) => {
    if (control.parentNode) {
      control.parentNode.removeChild(control);
    }
  });

  // Supprimer les éléments contenteditable
  const editableElements = canvasElement.querySelectorAll("[contenteditable]");
  editableElements.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.removeAttribute("contenteditable");
    }
  });

  try {
    // Attendre que le DOM soit mis à jour (comme dans SupportCanvas.vue)
    await new Promise((resolve) => setTimeout(resolve, 200));

    console.log("=== GÉNÉRATION HTML ===");
    console.log("Canvas:", canvasElement.outerHTML.length, "caractères");

    // Vérifier que le footer est présent
    const footerInCanvas = canvasElement.querySelector(".support-footer");
    console.log("Footer présent:", !!footerInCanvas);

    if (footerInCanvas) {
      console.log(
        "Contenu du footer:",
        footerInCanvas.innerHTML.length,
        "caractères"
      );
      console.log("Classes du footer:", footerInCanvas.className);
    }

    // Utiliser le HTML du canvas avec les styles inline appliqués
    let cleanHtml = cleanHtmlForEmail(canvasElement.outerHTML);

    // Convertir les URLs d'images en URLs absolues
    cleanHtml = convertImageUrlsToAbsolute(cleanHtml);

    // Générer le HTML email complet
    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title}</title>
      <style>
        /* Styles de base pour l'email */
        body { 
          margin: 0; 
          padding: 0; 
          font-family: Arial, sans-serif; 
          background-color: #f4f4f4;
          line-height: 1.6;
        }
        
        /* SUPPRIMER TOUTES LES BORDURES - Reset global */
        * {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
        
        /* Container principal - Taille A4 */
        .email-container { 
          width: 210mm; 
          min-height: 297mm;
          margin: 0 auto; 
          background-color: white;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          display: block;
        }
        
        /* Styles spécifiques pour le canvas */
        .support-canvas {
          width: 100% !important;
          min-height: 297mm !important;
          margin: 0 auto !important;
          display: flex !important;
          flex-direction: column !important;
          background: white !important;
          box-shadow: none !important;
        }
        
        /* Styles pour le header - CORRECTION POUR 100% */
        .support-header {
          width: 100% !important;
          text-align: center !important;
          padding: 0 !important;
          margin: 0 !important;
          background-color: white !important;
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
        
        .support-header img {
          width: 100% !important;
          height: auto !important;
          display: block !important;
          border: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        /* Styles pour le titre */
        .support-title-container {
          background-color: ${
            brand === "fendt" ? "#005e1e" : "#000000"
          } !important;
          color: white !important;
          padding: 15px !important;
          text-align: center !important;
          font-size: 20px !important;
          font-weight: bold !important;
        }
        
        .support-title-text {
          color: white !important;
          font-size: 20px !important;
          font-weight: bold !important;
          margin: 0 !important;
          background-color:black !important;
        }
        
        /* Styles pour le contenu */
        .support-content {
          flex: 1 !important;
          padding: 1.5rem !important;
          min-height: 200px !important;
          overflow: visible !important;
          background-color: white !important;
        }
        
        /* Styles pour les produits */
        .product-zone {
          border: none !important;
          border-radius: 0.5rem !important;
          display: flex !important;
          flex-direction: column !important;
          overflow: visible !important;
          min-height: 200px !important;
          width: 100% !important;
          padding: 0 !important;
          background-color: white !important;
          margin-bottom: 1rem !important;
          box-shadow: none !important;
          outline: none !important;
        }
        
        /* STYLES COMPLETS POUR LES BLOCS DE PRODUITS */
        .product-block {
          position: relative !important;
          width: 100% !important;
          height: 100% !important;
          background: white !important;
          border-radius: 0.5rem !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
        }
        
        .product-content {
          display: flex !important;
          flex-direction: row !important;
          padding: 0.75rem !important;
          gap: 1rem !important;
          width: 100% !important;
        }
        
        .product-content.image-right {
          flex-direction: row-reverse !important;
        }
        
        .image-section {
          flex: 0 0 50% !important;
          min-width: 200px !important;
          max-width: 350px !important;
          min-height: 250px !important;
          display: flex !important;
          align-items: center !important;
        }
        
        .info-section {
          flex: 1 !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 1rem !important;
          justify-content: center !important;
        }
        
        .image-container {
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          position: relative !important;
          padding: 0 !important;
          background: transparent !important;
          border-radius: 0.5rem !important;
          overflow: hidden !important;
          border: none !important;
          min-height: 250px !important;
        }
        
        .product-image {
          width: 100% !important;
          object-fit: cover !important;
          background: transparent !important;
          background-color: transparent !important;
        }
        
        .product-image.remove-background {
          mix-blend-mode: multiply !important;
          background: transparent !important;
          background-color: transparent !important;
        }
        
        .product-info {
          display: flex !important;
          flex-direction: column !important;
          gap: 0.5rem !important;
        }
        
        .product-title {
          font-weight: 600 !important;
          font-size: 1.8rem !important;
          line-height: 1.4 !important;
          margin-bottom: 0.5rem !important;
          color: #333 !important;
        }
        
        .product-description {
          font-size: 1.4rem !important;
          color: #666 !important;
          line-height: 1.6 !important;
          margin-bottom: 0.75rem !important;
          white-space: pre-wrap !important;
        }
        
        .price-section {
          margin-top: 0.5rem !important;
          display: flex !important;
          align-items: flex-start !important;
          gap: 1rem !important;
          flex-direction: row-reverse !important;
          justify-content: space-between !important;
        }
        
        .product-price {
          flex-grow: 0 !important;
          text-align: left !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-start !important;
        }
        
        .price-container {
          display: flex !important;
          align-items: center !important;
          gap: 1rem !important;
          justify-content: flex-start !important;
        }
        
        .price-ht-container {
          display: flex !important;
          align-items: baseline !important;
          gap: 0.5rem !important;
          transform-origin: left !important;
        }
        
        .price-ht {
          font-weight: 600 !important;
          color: #b70018 !important;
          display: inline-block !important;
        }
        
        .price-suffix.ht {
          color: #b70018 !important;
          font-weight: 500 !important;
          white-space: nowrap !important;
          display: inline-block !important;
          margin-left: 0.5rem !important;
        }
        
        .price-ttc-container {
          margin-top: 0.02rem !important;
          transform-origin: left !important;
          transform: scale(1) !important; /* Sera remplacé dynamiquement */
        }
        
        .price-ttc {
          color: #666 !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 0.25rem !important;
          white-space: nowrap !important;
        }
        
        .ttc-text {
          margin-left: 0.25rem !important;
        }
        
        /* Styles pour les états vides des images */
        .empty-state {
          color: #999 !important;
          font-size: 0.875rem !important;
          background: transparent !important;
        }
        
        /* Styles pour les pictogrammes */
        .picto-zone {
          position: absolute !important;
          top: 0.5rem !important;
          right: 0.5rem !important;
          width: 8rem !important;
          height: 3rem !important;
          display: flex !important;
          align-items: flex-start !important;
          justify-content: flex-end !important;
          background: transparent !important;
          border: none !important;
          border-radius: 0.5rem !important;
          padding: 0.25rem !important;
          z-index: 1 !important;
        }
        
        .picto-image {
          width: auto !important;
          height: 100% !important;
          object-fit: contain !important;
          mix-blend-mode: multiply !important;
          background: transparent !important;
          transition: transform 0.2s ease !important;
          transform-origin: top right !important;
        }
        
        /* Styles pour les marques */
        .brand-container {
          display: flex !important;
          margin-top: 1rem !important;
          margin-right: 5rem;
          justify-content: flex-end !important;
        }
        
        .brand-zone-info {
          width: 4rem !important;
          height: 2rem !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: transparent !important;
          border: none !important;
          border-radius: 0.5rem !important;
          padding: 0.25rem !important;
          position: relative !important;
        }
        
        .brand-image {
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important;
          mix-blend-mode: multiply !important;
          background: transparent !important;
          transform-origin: center !important;
        }
        
        .empty-state-preview {
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: transparent !important;
        }
        
        .default-product-svg {
          width: 60% !important;
          height: 60% !important;
          max-width: 100px !important;
          max-height: 100px !important;
          opacity: 0.5 !important;
        }
        
        /* Styles pour le footer */
        .support-footer {
          background-color: white !important;
          padding: 0 !important;
          border: none !important;
        }
        
        /* Styles pour la validité */
        .validity-banner {
          background-color: ${
            brand === "fendt" ? "#005e1e" : "#000000"
          } !important;
          color: white !important;
          padding: 15px !important;
          text-align: center !important;
          font-size: 16px !important;
          font-weight: 500 !important;
        }
        
        /* Styles pour les images */
        img {
          max-width: 100%;
          height: auto;
          display: block;
          border: 0;
        }
        
        /* Styles pour les liens */
        a {
          color: #B70018;
          text-decoration: none;
        }
        
        a:hover {
          text-decoration: underline;
        }
        
        /* Styles pour les boutons */
        button {
          border: none;
          background: none;
          cursor: pointer;
        }
        
        /* Styles pour les inputs */
        input, textarea {
          border: 1px solid #ddd;
          padding: 8px;
          border-radius: 4px;
          font-family: inherit;
          font-size: 14px;
        }
        
        /* Styles pour les réseaux sociaux */
        .social-links {
          display: flex !important;
          justify-content: center !important;
          gap: 20px !important;
          margin: 20px 0 !important;
        }
        
        .social-links svg {
          width: 24px !important;
          height: 24px !important;
        }
        
        .social-links a {
          color: #B70018 !important;
          text-decoration: none !important;
        }
        
        /* Styles pour le footer complet */
        .support-footer {
          background-color: white !important;
          padding: 0 !important;
          border: none !important;
        }
        
        .support-footer .bg-white {
          background-color: white !important;
          padding: 2rem !important;
        }
        
        .support-footer .flex {
          display: flex !important;
        }
        
        .support-footer .flex-col {
          flex-direction: column !important;
        }
        
        .support-footer .items-center {
          align-items: center !important;
        }
        
        .support-footer .gap-4 {
          gap: 1rem !important;
        }
        
        .support-footer .gap-6 {
          gap: 1.5rem !important;
        }
        
        .support-footer .mb-4 {
          margin-bottom: 1rem !important;
        }
        
        .support-footer .text-center {
          text-align: center !important;
        }
        
        .support-footer .text-sm {
          font-size: 0.875rem !important;
        }
        
        .support-footer .text-gray-600 {
          color: #666 !important;
        }
        
        .support-footer .space-y-2 > * + * {
          margin-top: 0.5rem !important;
        }
        
        .support-footer .w-3\\/5 {
          width: 60% !important;
        }
        
        .support-footer .mx-auto {
          margin-left: auto !important;
          margin-right: auto !important;
        }
        
        .support-footer .h-12 {
          height: 3rem !important;
        }
        
        .support-footer .w-6 {
          width: 1.5rem !important;
        }
        
        .support-footer .h-6 {
          height: 1.5rem !important;
        }
        
        /* Styles pour les icônes SVG */
        svg {
          width: 24px !important;
          height: 24px !important;
          fill: currentColor !important;
        }
        
        /* Styles pour les mentions légales */
        .disclaimer {
          font-size: 12px;
          color: #666;
          line-height: 1.4;
          margin-top: 20px;
        }
        
        /* Styles pour le footer */
        .footer-content {
          padding: 20px;
          text-align: center;
        }
        
        /* Styles pour les produits - SUPPRIMÉS (déjà définis plus haut) */
        
        /* Styles pour le header */
        .email-header {
          text-align: center;
          padding: 20px;
        }
        
        /* Styles pour le titre */
        .email-title {
          background-color: ${brand === "fendt" ? "#005e1e" : "#000000"};
          color: white;
          padding: 15px;
          text-align: center;
          font-size: 20px;
          font-weight: bold;
        }
        
        /* Styles pour la validité */
        .validity-banner {
          background-color: ${brand === "fendt" ? "#005e1e" : "#000000"};
          color: white;
          padding: 15px;
          text-align: center;
          font-size: 16px;
          font-weight: 500;
        }
        
        /* Styles pour le repiquage */
        .repiquage-container {
          text-align: center;
          padding: 20px;
        }
        
        .repiquage-logo {
          max-width: 400px;
          height: auto;
        }
        
        /* Styles pour les tableaux (compatibilité email) */
        table {
          border-collapse: collapse;
          width: 100%;
        }
        
        td, th {
          padding: 8px;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }
        
        /* Responsive design */
        @media only screen and (max-width: 600px) {
          .email-container {
            width: 100% !important;
            max-width: 100% !important;
          }
          
          .social-links {
            flex-direction: column;
            align-items: center;
            gap: 10px;
          }
          
          .product-block {
            padding: 10px;
          }
          
          .email-title {
            font-size: 18px;
            padding: 12px;
          }
          
          .validity-banner {
            font-size: 14px;
            padding: 12px;
          }
          
          .picto-zone {
            width: 5rem !important;
            height: 2.5rem !important;
            top: 0.25rem !important;
            right: 0.25rem !important;
          }
        }
        
        /* Styles spécifiques pour les clients email */
        .ExternalClass {
          width: 100%;
        }
        
        .ExternalClass,
        .ExternalClass p,
        .ExternalClass span,
        .ExternalClass font,
        .ExternalClass td,
        .ExternalClass div {
          line-height: 100%;
        }
        
        /* Styles pour Outlook */
        table {
          mso-table-lspace: 0pt;
          mso-table-rspace: 0pt;
        }
        
        img {
          -ms-interpolation-mode: bicubic;
        }
      </style>
    </head>
    <body>
      <div class="email-container">
        ${cleanHtml}
      </div>
    </body>
    </html>
  `;

    return htmlContent;
  } finally {
    // Restaurer l'état initial (comme dans SupportCanvas.vue)
    canvasElement.classList.remove("pdf-capture-mode");
    if (canvasElement) {
      canvasElement.style.backgroundColor =
        originalStyles.backgroundColor || "";
      canvasElement.style.height = originalStyles.height || "";
      canvasElement.style.overflow = originalStyles.overflow || "";
      canvasElement.style.maxHeight = originalStyles.maxHeight || "";
      canvasElement.style.position = originalStyles.position || "";
      canvasElement.style.top = originalStyles.top || "";
      canvasElement.style.left = originalStyles.left || "";
      canvasElement.style.transform = originalStyles.transform || "";
    }
  }
};
